// src/context/AuthContext.tsx
import type { User } from 'firebase/auth';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import LoadingPage from '../components/LoadingPage';
import { useAppDependencies } from './AppDependenciesContext';
import { setUserContext } from '../sentry';

const TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

// Sentinel value to distinguish "never initialized" from "user is null".
// This lets us detect the very first onIdTokenChanged call unambiguously.
const UNINITIALIZED = Symbol('uninitialized');

interface IAuthContext {
  currentUser: User | null;
  isAdmin: boolean;
  isAnnotator: boolean;
  loading: boolean;
  isOffline: boolean;
}

const AuthContext = createContext<IAuthContext>({
  currentUser: null,
  isAdmin: false,
  isAnnotator: false,
  loading: true,
  isOffline: false,
});

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const {
    authSession: {
      getCurrentUser,
      getIdTokenResult: getIdTokenResultFromGateway,
      onIdTokenChanged: onIdTokenChangedFromGateway,
    },
  } = useAppDependencies();

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAnnotator, setIsAnnotator] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Tracks the UID seen on the last onIdTokenChanged invocation.
  // UNINITIALIZED = first call has not yet arrived.
  // null = last known state was logged-out.
  // string = UID of the currently authenticated user.
  const prevUserUidRef = useRef<string | null | typeof UNINITIALIZED>(UNINITIALIZED);

  // Timestamp of the last successful getIdTokenResult call.
  // Reset to 0 on logout so the next login always fetches fresh claims.
  const lastRefreshRef = useRef<number>(0);

  /**
   * Fetches fresh custom claims and updates role state.
   * Rate-limited by TOKEN_REFRESH_THRESHOLD_MS to avoid redundant network calls.
   *
   * IMPORTANT: this function deliberately does NOT touch currentUser.
   * Caller is responsible for setting currentUser before or after this call.
   */
  const refreshClaims = useCallback(
    async (user: User): Promise<void> => {
      const now = Date.now();
      if (now - lastRefreshRef.current < TOKEN_REFRESH_THRESHOLD_MS) {
        return;
      }
      // Optimistic lock: prevents concurrent calls from all making a network request.
      // Rolled back on failure so the next attempt is not blocked by the threshold.
      const previousRefresh = lastRefreshRef.current;
      lastRefreshRef.current = now;
      try {
        const tokenRes = await getIdTokenResultFromGateway(user, true);
        const claims = tokenRes.claims;
        setIsAdmin(Boolean(claims.admin));
        setIsAnnotator(Boolean(claims.annotator));
      } catch (error) {
        lastRefreshRef.current = previousRefresh;
        throw error;
      }
    },
    [getIdTokenResultFromGateway]
  );

  useEffect(() => {
    const unsub = onIdTokenChangedFromGateway(async (user) => {
      const currentUid = user?.uid ?? null;
      const prevUid = prevUserUidRef.current;

      // Detect whether auth *identity* changed:
      //   - UNINITIALIZED → any  (initial resolution on mount)
      //   - null           → uid  (login)
      //   - uid            → null (logout)
      //   - uid-A          → uid-B (account switch — rare but possible)
      // A same-uid call with a different token is a silent Firebase auto-refresh
      // (~hourly). We must NOT show a loading screen in that case.
      const isIdentityChange = prevUid === UNINITIALIZED || prevUid !== currentUid;

      if (isIdentityChange) {
        setLoading(true);
      }
      prevUserUidRef.current = currentUid;

      try {
        if (user) {
          // Set the user immediately so the <Navigate> guard in LoginPage and
          // ProtectedRoute react as soon as the loading gate is lowered.
          setCurrentUser(user);

          // Fetch fresh claims. Isolated try/catch so a transient network error
          // does NOT log out a confirmed-authenticated user. The worst case is
          // stale roles until the next successful refresh.
          try {
            await refreshClaims(user);
          } catch {
            // Claims fetch failed transiently. User remains authenticated.
            // Roles keep their previous values (or defaults on first login).
            // onIdTokenChanged will fire again on the next auto-refresh (~1h).
          }
        } else {
          // Full logout: reset every piece of auth state and the rate-limit
          // timestamp so the next login always fetches fresh claims.
          lastRefreshRef.current = 0;
          setCurrentUser(null);
          setIsAdmin(false);
          setIsAnnotator(false);
        }
      } catch (unexpectedError) {
        // Defensive: synchronous React setters don't throw in practice.
        console.error('[AuthContext] Unexpected error in onIdTokenChanged:', unexpectedError);
      } finally {
        // Only release the loading gate for identity-change calls, matching
        // the setLoading(true) above. Token refreshes for the same user never
        // touch the loading state, so there is no full-screen flash every hour.
        if (isIdentityChange) {
          setLoading(false);
        }
      }
    });
    return unsub;
  }, [onIdTokenChangedFromGateway, refreshClaims]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      const currentUser = getCurrentUser();
      if (document.visibilityState === 'visible' && currentUser) {
        try {
          await refreshClaims(currentUser);
        } catch {
          // Token refresh failed; onIdTokenChanged will handle re-auth
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [getCurrentUser, refreshClaims]);

  useEffect(() => {
    const handleOnline = async () => {
      setIsOffline(false);
      const currentUser = getCurrentUser();
      if (currentUser) {
        try {
          await refreshClaims(currentUser);
        } catch {
          // Token refresh failed on reconnection; onIdTokenChanged will handle
        }
      }
    };

    const handleOffline = () => {
      setIsOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [getCurrentUser, refreshClaims]);

  useEffect(() => {
    if (currentUser) {
      setUserContext({
        id: currentUser.uid,
      });
    } else {
      setUserContext(null);
    }
  }, [currentUser]);

  if (loading) {
    return <LoadingPage minHeight="100vh" />;
  }

  return (
    <AuthContext.Provider value={{ currentUser, isAdmin, isAnnotator, loading, isOffline }}>
      {children}
    </AuthContext.Provider>
  );
};
